export type TourStepKey = 'logo' | 'xero' | 'commission' | 'wishlist' | 'form6' | 'viewClient' | 'viewClientWishlist'

export interface OnboardingTourStep {
    stepNumber: number
    description: string
    tourId?: string
    hint?: string
    noAutoAdvance?: boolean
    /** Auto-advance to the next step after this many milliseconds. */
    autoAdvanceMs?: number
    doneWhenRoute?: string
    /** Default values for query params missing from the URL (e.g. the default tab). */
    doneWhenRouteDefaults?: Record<string, string>
    doneWhenMissing?: string
    /** Skip this step when this data-tour element IS in the DOM (e.g. already on the right tab). */
    doneWhenPresent?: string
}

// Check if the current location matches or is deeper than a target path
// Supports query param matching (e.g. ?tab=Xero)
// `defaults` supplies fallback values for query params missing from the URL (e.g. the default tab)
export const routeMatches = (
    target: string,
    location: { pathname: string; search: string },
    defaults?: Record<string, string>,
): boolean => {
    try {
        const url = new URL(target, 'http://localhost')
        const targetPath = url.pathname
        // Check pathname matches or is deeper
        if (location.pathname !== targetPath && !location.pathname.startsWith(targetPath + '/')) {
            return false
        }
        // Check query params if specified
        const currentParams = new URLSearchParams(location.search)
        for (const [key, value] of url.searchParams.entries()) {
            const currentValue = currentParams.get(key) ?? defaults?.[key]
            if (currentValue !== value) return false
        }
        return true
    } catch {
        return false
    }
}

interface StepOpts {
    orgId: string
    xeroConnected?: boolean
    firstPurchaserId?: string
    userId?: string
}

// Each tour is a flat list of steps with declarative completion conditions.
// syncTourStep iterates from minStepIndex, skipping steps whose doneWhen* condition is met.
const tourSteps: Record<TourStepKey, (opts: StepOpts) => OnboardingTourStep[]> = {
    logo: (opts) => [
        {
            stepNumber: 1,
            description: 'Click the gear icon in the top-right to open Settings.',
            tourId: 'nav-settings',
            doneWhenRoute: `/${opts.orgId}/settings`,
        },
        {
            stepNumber: 2,
            description: 'Click the Organisation Settings tab.',
            tourId: 'settings-tab-org',
            doneWhenRoute: `/${opts.orgId}/settings?tab=Organisation Settings`,
            doneWhenRouteDefaults: { tab: 'Organisation Settings' },
        },
        {
            stepNumber: 3,
            description: 'Click Upload Logo to add your company logo.',
            tourId: 'logo-upload',
        },
    ],
    xero: (opts) => {
        const steps: OnboardingTourStep[] = [
            {
                stepNumber: 1,
                description: 'Click the gear icon in the top-right to open Settings.',
                tourId: 'nav-settings',
                doneWhenRoute: `/${opts.orgId}/settings`,
            },
            {
                stepNumber: 2,
                description: 'Click the Xero tab.',
                tourId: 'settings-tab-xero',
                doneWhenRoute: `/${opts.orgId}/settings?tab=Xero`,
            },
        ]
        if (opts.xeroConnected) {
            steps.push({
                stepNumber: 3,
                description: 'Select your engagement and commission invoice items.',
                tourId: 'xero-config',
            })
        } else {
            steps.push({
                stepNumber: 3,
                description: "Click Connect to link your Xero account. You'll be redirected to authorise the connection.",
                tourId: 'xero-connect',
            })
        }
        return steps
    },
    commission: (opts) => [
        {
            stepNumber: 1,
            description: 'Click the gear icon in the top-right to open Settings.',
            tourId: 'nav-settings',
            doneWhenRoute: `/${opts.orgId}/settings`,
        },
        {
            stepNumber: 2,
            description: 'Click the Users tab.',
            tourId: 'settings-tab-users',
            doneWhenRoute: `/${opts.orgId}/settings/users`,
        },
        {
            stepNumber: 3,
            description: 'Select your profile from the list.',
            tourId: 'current-user-row',
            doneWhenMissing: 'current-user-row',
        },
        {
            stepNumber: 4,
            description: 'Enter your engagement fee amount. This is the fee automatically invoiced when a client signs their Agent Appointment form.',
            tourId: 'user-engagement-fee',
            hint: 'Make sure the Sales Agent checkbox is ticked to see the engagement fee field.',
            autoAdvanceMs: 1500,
        },
        {
            stepNumber: 5,
            description: 'Click Save to store your engagement fee.',
            tourId: 'user-save-button',
        },
    ],
    wishlist: () => [],
    form6: (opts) => [
        {
            stepNumber: 1,
            description: 'Click Clients in the top bar.',
            tourId: 'nav-clients',
            doneWhenRoute: `/${opts.orgId}/clients`,
        },
        {
            stepNumber: 2,
            description: 'Click on your test client to open their record.',
            tourId: 'first-purchaser-row',
            doneWhenRoute: opts.firstPurchaserId
                ? `/${opts.orgId}/clients/${opts.firstPurchaserId}`
                : undefined,
        },
        {
            stepNumber: 3,
            description: 'Click the Edit button to switch to edit mode.',
            tourId: 'client-edit-button',
            doneWhenMissing: 'client-edit-button',
        },
        {
            stepNumber: 4,
            description: 'Enter an authority start date and duration, then scroll down to the Form 6 section.',
            tourId: 'authority-section',
            noAutoAdvance: true,
            autoAdvanceMs: 1500,
            doneWhenPresent: 'authority-filled',
        },
        {
            stepNumber: 5,
            description: 'Click Create Form, review the pre-filled fields, then send for signing. Both parties will receive signing emails.',
            tourId: 'create-form-button',
            hint: 'Review the pre-filled fields before sending.',
            doneWhenMissing: 'create-form-button',
        },
    ],
    viewClient: (opts) => [
        {
            stepNumber: 1,
            description: 'Click Clients in the top bar.',
            tourId: 'nav-clients',
            doneWhenRoute: `/${opts.orgId}/clients`,
        },
        {
            stepNumber: 2,
            description: 'Click on your test client to open their record.',
            tourId: 'first-purchaser-row',
            doneWhenRoute: opts.firstPurchaserId
                ? `/${opts.orgId}/clients/${opts.firstPurchaserId}`
                : undefined,
        },
        {
            stepNumber: 3,
            description: 'Click the Wishlist tab to see the data your client submitted.',
            tourId: 'purchaser-tab-wishlist',
            doneWhenRoute: opts.firstPurchaserId
                ? `/${opts.orgId}/clients/${opts.firstPurchaserId}/wishlist`
                : undefined,
        },
    ],
    viewClientWishlist: () => [
        {
            stepNumber: 1,
            description: 'Click the Wishlist tab to see what your client submitted.',
            tourId: 'purchaser-tab-wishlist',
        },
        {
            stepNumber: 2,
            description: "This is your client's wishlist — their preferences, budget, and property requirements. You can edit this anytime.",
        },
    ],
}

/**
 * Returns a flat array of tour steps with declarative completion conditions.
 * Step numbers are re-assigned sequentially.
 */
export const getOnboardingTourSteps = (
    stepKey: TourStepKey,
    orgId: string,
    opts: {
        xeroConnected?: boolean
        firstPurchaserId?: string
        userId?: string
    },
): OnboardingTourStep[] => {
    const steps = tourSteps[stepKey]({ orgId, ...opts })
    return steps.map((step, i) => ({ ...step, stepNumber: i + 1 }))
}
